import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, CloseButton, Container, Flex, FormControl,
  FormLabel, Heading, HStack, IconButton, Image, Input, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, List, ListIcon, ListItem, Select, Spinner, Stack, Switch,
  Text, Textarea, useDisclosure, useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PictoolsMark } from '../components/PictoolsMark';
import { failArtFor } from '../components/failArt';
import { useDispatch, useSelector } from 'react-redux';
import { ID3Writer } from 'browser-id3-writer';
import { catalogCoverImage } from '../catalog/assets';
import { useAuth } from '../auth/AuthProvider';
import { useCreateJobMutation, useDraftLyricsMutation, useGetJobQuery, useInspireFromImageMutation, useLazyGetEmbedPendingQuery, PresetDoc } from '../redux/services/mixgenie';
import { resizeImageForUpload } from '../download/resizeImage';
import { mixgeniePublicServiceURL } from '../redux/services/defines';
import { downloadBlobFromUrl } from '../download/downloadBlob';
import { PresetPicker } from '../generator/PresetPicker';
import { PresetChips } from '../generator/PresetChips';
import { GenerationOverlay } from '../generator/GenerationOverlay';
import { ReferenceSearch } from '../generator/ReferenceSearch';
import { MicRecorderModal } from '../library/MicRecorderModal';
import { AudioClipPickerModal } from '../library/AudioClipPickerModal';
import { RootState } from '../redux/store';
import { addTracks, setReferenceId } from '../redux/features/localAudioSlice';
import { setQueue, updateTrack } from '../redux/features/playerSlice';

/**
 * Reference audio ships to PromptLab which forwards to Mureka via multipart
 * → an S3 upload PromptLab signs server-side. Two things have to be tight
 * or the S3 signature calculation breaks and we get "SignatureDoesNotMatch":
 *
 *   1. Filename MUST be ASCII-only, no unicode / no ellipsis / no `<>:"|?*`.
 *      Web Share Target hands us the OS-picked filename which often carries
 *      unicode (…, curly quotes, non-latin chars from streaming apps) that
 *      trips PromptLab's Node form-data canonicalization vs. AWS SigV4.
 *
 *   2. Size cap ~ 8 MB. Mureka only uses the first ~60 s as a musical
 *      reference anyway; shipping a whole album track wastes upload time
 *      and has repeatedly correlated with 5xx from PromptLab's S3 stage.
 *      MP3 is stream-tolerant to truncation (frames are self-contained),
 *      so byte-slicing the head of the file gives a valid, short mp3.
 */
const REFERENCE_MAX_BYTES = 8 * 1024 * 1024;

function sanitizeReferenceFileName(name: string, mime: string): string {
  const ext = mime === 'audio/mpeg' ? '.mp3'
    : mime === 'audio/mp4' ? '.m4a'
    : mime === 'audio/wav' ? '.wav'
    : mime === 'audio/ogg' ? '.ogg'
    : /\.\w{2,4}$/.test(name) ? name.slice(name.lastIndexOf('.')) : '.mp3';
  const stem = (name || 'reference')
    .replace(/\.[^.]+$/, '')                // drop existing extension
    .normalize('NFKD')                       // decompose accented chars
    .replace(/[̀-ͯ]/g, '')          // strip combining marks after NFKD
    .replace(/[^\x20-\x7E]+/g, '')           // drop any remaining non-ASCII
    .replace(/[<>:"|?*\\/]+/g, ' ')          // drop path/filesystem specials
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60) || 'reference';
  return `${stem}${ext}`;
}

// ISO-code → full English name that the LLM prompt templates expect.
// Kept as a plain map (small, closed set) to avoid a runtime dep on
// Intl.DisplayNames for such a short list.
const LANG_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
};

async function fileToReferenceAudio(file: File): Promise<{ fileName: string; mimeType: string; base64: string; size: number }> {
  let buf = await file.arrayBuffer();
  // Head-clip long files. MP3 tolerates byte-truncation; other codecs will
  // usually decode up to the last complete frame in the truncated buffer.
  if (buf.byteLength > REFERENCE_MAX_BYTES) {
    buf = buf.slice(0, REFERENCE_MAX_BYTES);
  }
  const bytes = new Uint8Array(buf);
  // btoa can't handle raw binary strings above one octet — batch through
  // Uint8Array then convert to a binary string chunk by chunk to keep the
  // stack small for larger files.
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  const mimeType = file.type || 'audio/mpeg';
  return {
    fileName: sanitizeReferenceFileName(file.name, mimeType),
    mimeType,
    base64: btoa(binary),
    size: bytes.length,
  };
}

// Download a MixGenie song as an mp3 straight from media.verna.one (CORS-open).
// The BE proxy path (main-service public-gateway → mixgenie-service) corrupts
// the binary — axios in main-service parses the response as JSON/text and
// UTF-8-encodes it, producing a ~1.77× oversized, unplayable file. Fetching
// the CDN URL directly and delivering via a same-origin blob:// anchor keeps
// the mobile-Chrome download working without going through the gateway.
async function downloadSongWithTags(audioUrl: string | undefined, title: string): Promise<void> {
  if (!audioUrl) return;
  const filename = (title || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80).trim() || 'song';
  await downloadBlobFromUrl(audioUrl, `${filename}.mp3`);
}

/**
 * Generator page — deliberately minimal:
 *   1. optional prompt
 *   2. optional lyrics + "instrumental" checkbox (mutually exclusive)
 *   3. "Selection" strip (reference song chip + prompt preview) — the
 *      pictools-parallel to the effect/prompt filter zone
 *   4. Generate button
 *
 * If the visitor arrived from a song page (via `generatorReferencePath`),
 * the reference chip is pre-filled from URL params. The chip is removable —
 * removing it makes this a from-scratch generation.
 */
export default function GeneratorPage() {
  const { t, i18n } = useTranslation();
  // Language of the lyrics — separate from the app UI language so a user
  // browsing the Spanish UI can still generate an English song. Default to
  // the app's current locale (i18n.language, ISO code); the picker below
  // lets the user override before drafting or generating.
  const initialLyricLang = (i18n.language || 'es').slice(0, 2).toLowerCase();
  const [lyricsLanguage, setLyricsLanguage] = useState<string>(
    ['en', 'es', 'pt'].includes(initialLyricLang) ? initialLyricLang : 'es',
  );
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const paywall = useDisclosure();

  // --- Reference (from ?referenceTitle=&referenceArtist=…) --------------
  const initialRef = useMemo(() => {
    const title = params.get('referenceTitle') || '';
    const artist = params.get('referenceArtist') || '';
    if (!title && !artist) return null;
    return {
      title,
      artist,
      slug: params.get('referenceSlug') || '',
      artistSlug: params.get('referenceArtistSlug') || '',
      cover: params.get('referenceCover') || '',
      styleNotes: params.get('styleNotes') || '',
    };
  }, [params]);

  const [reference, setReference] = useState(initialRef);
  const dispatch = useDispatch();
  // Local reference file picked from the device library (Redux slice; File
  // object held in memory). When set, it wins over the catalog reference —
  // we ship the user's own audio to Mureka instead of the iTunes preview.
  const localReference = useSelector((s: RootState) => {
    const id = s.localAudio.referenceId;
    if (!id) return null;
    return s.localAudio.tracks.find((t) => t.id === id) || null;
  });
  const clearLocalReference = () => dispatch(setReferenceId(null));
  const micRecorder = useDisclosure();
  const [prompt, setPrompt] = useState<string>(params.get('vibe') || params.get('prompt') || '');
  // Seed lyrics from route state — HistoryPage's "New song with these
  // lyrics" button navigates here with `{ state: { lyrics } }`. Lyrics live
  // in state rather than the URL because they can be a few thousand chars,
  // which would blow past URL length limits and break some proxies. The
  // one-shot consume-and-clear pattern below prevents the seed from
  // re-applying on remounts / hot-reload.
  const location = useLocation();
  const seededLyricsRef = useRef(false);
  const initialLyrics = !seededLyricsRef.current && typeof (location.state as any)?.lyrics === 'string'
    ? (location.state as any).lyrics as string
    : '';
  if (initialLyrics) seededLyricsRef.current = true;
  const [lyrics, setLyrics] = useState<string>(initialLyrics);
  const [instrumental, setInstrumental] = useState<boolean>(false);
  // Opt-in cover art with the song (+1 credit → 2 total). Never on by default.
  const [withCover, setWithCover] = useState<boolean>(false);
  // Applied presets — one array per target. `promptFragment`s from `soundPresets`
  // are joined to the style prompt sent to Mureka; `lyricsPresets` become
  // a thematic brief prepended to the lyrics prompt (or to the Draft-lyrics
  // request when the user hits ✨ Draft lyrics).
  const [soundPresets, setSoundPresets] = useState<PresetDoc[]>([]);
  const [lyricsPresets, setLyricsPresets] = useState<PresetDoc[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Free-tier queue-cap hit — surfaced via a modal that upsells rather
  // than a generic error toast. `null` when the cap hasn't been triggered.
  const [queueFull, setQueueFull] = useState<null | { queued?: number; limit?: number }>(null);

  const [createJob] = useCreateJobMutation();
  const [draftLyrics, { isLoading: isDraftingLyrics }] = useDraftLyricsMutation();
  const [inspireFromImage, { isLoading: isInspiring }] = useInspireFromImageMutation();
  const [fetchEmbedPending] = useLazyGetEmbedPendingQuery();

  // Hydration from a widget hand-off. Third-party embeds (mixgenie.ai
  // homepage, /features/*, dicelacancion.com song pages) POST the
  // anonymous inputs to /public/embed-pending → { id }, then bounce the
  // visitor to /generator?pending=<id>. We fetch the payload, prefill
  // the form so it's exactly what they typed on the marketing page, and
  // strip the query so a refresh doesn't re-hydrate. Runs once per
  // pending id — the ref guards against React Strict-mode double-fires
  // and against any post-hydrate URL mutations that would otherwise
  // trigger another effect run.
  const hydratedPendingRef = useRef<string | null>(null);
  useEffect(() => {
    // Pending id survives the login/signup round-trip via localStorage.
    // If it's on the URL, that's the freshest source (widget just handed
    // us off). Otherwise, check the stash — Google OAuth callback lands
    // on `/` and would otherwise lose the id (AuthCallbackPage has no
    // way to carry query state through Cognito). AuthCallbackPage now
    // routes signed-in users straight to `/generator?pending=<id>` when
    // the stash is populated; this effect covers the other end.
    let pendingId = params.get('pending');
    if (!pendingId) {
      try {
        const stored = localStorage.getItem('mg.pending.id');
        const exp = Number(localStorage.getItem('mg.pending.exp') || '0');
        if (stored && /^[a-f0-9]{24}$/.test(stored) && exp > Date.now()) {
          pendingId = stored;
        } else if (stored) {
          localStorage.removeItem('mg.pending.id');
          localStorage.removeItem('mg.pending.exp');
        }
      } catch { /* ignore */ }
      if (pendingId) {
        // Promote to URL so the rest of the effect (and any refresh)
        // treats it as the canonical source. Preserve other params.
        const next = new URLSearchParams(params);
        next.set('pending', pendingId);
        setParams(next, { replace: true });
        return; // rerun with pending in the URL
      }
    }
    if (!pendingId || hydratedPendingRef.current === pendingId) return;
    hydratedPendingRef.current = pendingId;
    // Clear the stash on the first legit hydration attempt — even if the
    // fetch fails below, we don't want to loop or resurrect a stale id.
    try { localStorage.removeItem('mg.pending.id'); localStorage.removeItem('mg.pending.exp'); } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchEmbedPending(pendingId).unwrap();
        if (cancelled || !payload) return;
        if (payload.prompt) setPrompt(payload.prompt);
        if (payload.lyrics) setLyrics(payload.lyrics);
        if (payload.instrumental) setInstrumental(true);
        if (payload.language && ['en', 'es', 'pt'].includes(payload.language)) {
          setLyricsLanguage(payload.language);
        }
        if (payload.mode === 'reference' && (payload.referenceSongTitle || payload.referenceArtistName)) {
          setReference({
            title: payload.referenceSongTitle || '',
            artist: payload.referenceArtistName || '',
            slug: payload.referenceSongSlug || '',
            artistSlug: payload.referenceArtistSlug || '',
            cover: payload.referenceCoverUrl || '',
            styleNotes: '',
          });
        }
        if (payload.mode === 'audio' && payload.referenceAudioDataUrl) {
          // Widget uploaded a reference song; decode → File → local-audio
          // slice. addTracks + setReferenceId mirrors the in-app device
          // picker so the generator's existing reference chip renders it.
          try {
            const dataUrl = payload.referenceAudioDataUrl;
            const commaIdx = dataUrl.indexOf(',');
            const mime = payload.referenceAudioMimeType
              || (dataUrl.slice(5, commaIdx).split(';')[0]) // parse "data:audio/mpeg;base64"
              || 'audio/mpeg';
            const b64 = commaIdx > 0 ? dataUrl.slice(commaIdx + 1) : '';
            const bin = atob(b64);
            const buf = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
            const fname = (payload.referenceAudioFileName || 'reference').replace(/[^\w.\- ]+/g, '_');
            const file = new File([buf], fname, { type: mime });
            const trackId = `widget-${payload.id}`;
            dispatch(addTracks([{
              id: trackId,
              file,
              name: fname,
              size: file.size,
              addedAt: Date.now(),
            }]));
            dispatch(setReferenceId(trackId));
          } catch { /* silent — user can still upload manually */ }
        }
        if (payload.mode === 'image' && payload.imageDataUrl) {
          // Fire the vision brief straight away so the user lands on a
          // ready-to-generate form instead of a bare image thumb.
          try {
            const lang = LANG_NAMES[payload.language || lyricsLanguage] || 'English';
            const res = await inspireFromImage({ imageDataUrl: payload.imageDataUrl, language: lang }).unwrap();
            const brief = res.data;
            if (!cancelled) {
              setPrompt(brief.stylePrompt || payload.prompt || '');
              if (!payload.instrumental) setLyrics(brief.lyrics || '');
              setInspireDescription(brief.description || '');
            }
          } catch { /* fall through — the prompt (if any) still landed */ }
        }
        // Strip ?pending= so a page refresh doesn't re-trigger. Preserve
        // any other query params (e.g. ?shared=1 from the share-target).
        const next = new URLSearchParams(params);
        next.delete('pending');
        setParams(next, { replace: true });
      } catch {
        // Pending id expired (24h TTL) or the service was momentarily
        // down. Silent fail — the generator still works with an empty
        // form and the visitor can fill it in manually.
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch fn is stable
  }, [params]);

  // Hidden file input for the "🖼️ From an image" starting point. Uses
  // capture="environment" so mobile OSes offer camera alongside gallery.
  const inspireImageInput = useRef<HTMLInputElement | null>(null);
  const [inspireDescription, setInspireDescription] = useState<string>('');
  const pickInspirationImage = () => inspireImageInput.current?.click();
  const onInspirationImageChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast({ title: t('generator.imageTooLarge', 'Image too large (max 12 MB)'), status: 'warning' });
      return;
    }
    try {
      // Shrink first — vision models cap payload at a few MB anyway and
      // Sonnet's per-image token cost tracks with the encoded size.
      // 1600px is plenty for scene interpretation.
      const dataUrl = await resizeImageForUpload(file, 1600);
      const language = LANG_NAMES[lyricsLanguage] || 'English';
      const res = await inspireFromImage({ imageDataUrl: dataUrl, language }).unwrap();
      const brief = res.data;
      // Fill only what the image drove — leave presets and reference song
      // as-is so the user can layer extras on top. Instrumental toggle is
      // deliberately left alone: if they set it before uploading, respect
      // that; the AI still returns lyrics, but we won't force them in.
      setPrompt(brief.stylePrompt || '');
      if (!instrumental) setLyrics(brief.lyrics || '');
      setInspireDescription(brief.description || '');
      toast({
        title: t('generator.inspireDone', 'Song brief drafted from image'),
        description: brief.suggestedTitle ? `"${brief.suggestedTitle}"` : undefined,
        status: 'success',
        duration: 3500,
      });
    } catch (err: any) {
      toast({
        title: t('generator.inspireFail', 'Could not interpret the image'),
        description: String(err?.data?.message || err?.message || 'Try a clearer photo'),
        status: 'error',
        duration: 3500,
      });
    }
  };
  // Reference-audio picker — retired the standalone Library page in favour
  // of an inline device-file picker on the generator itself. Small files
  // (≤3 MB, ~<3 min at 128 kbps) go straight to the reference chip; larger
  // files open the clip picker so the user picks the 60 s window we ship
  // to Mureka (matching the old LibraryPage flow).
  const referenceFileInput = useRef<HTMLInputElement | null>(null);
  const CLIP_PICKER_MIN_BYTES = 3 * 1024 * 1024;
  const [clipPending, setClipPending] = useState<null | { id: string; name: string; file: File }>(null);

  const attachLocalReferenceFile = (file: File) => {
    const id = `pick-${Date.now()}`;
    dispatch(addTracks([{
      id,
      file,
      name: file.name,
      size: file.size,
      addedAt: Date.now(),
    }]));
    dispatch(setReferenceId(id));
    setReference(null);
    toast({
      title: t('generator.referenceAttached', 'Reference attached'),
      description: file.name,
      status: 'success',
      duration: 2200,
    });
  };

  const onReferenceFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size >= CLIP_PICKER_MIN_BYTES) {
      setClipPending({ id: `pick-${Date.now()}`, name: file.name, file });
      return;
    }
    attachLocalReferenceFile(file);
  };

  // Drain the PWA share_target inbox on ?shared=1 — the sw.ts fetch
  // handler stashes files here and redirects here. First file wins as
  // the reference (same UX shape as picking a single track manually).
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('shared');
    if (!p) return;
    (async () => {
      try {
        if (!('caches' in window)) return;
        const cache = await caches.open('mixgenie-shared-inbox');
        const keys = await cache.keys();
        for (const key of keys) {
          const res = await cache.match(key);
          if (!res) { await cache.delete(key); continue; }
          const blob = await res.blob();
          const name = res.headers.get('X-Mixgenie-Filename') || 'shared-audio.mp3';
          const file = new File([blob], name, { type: blob.type || 'audio/mpeg' });
          await cache.delete(key);
          if (file.size >= CLIP_PICKER_MIN_BYTES) {
            setClipPending({ id: `share-${Date.now()}`, name: file.name, file });
          } else {
            attachLocalReferenceFile(file);
          }
          break;
        }
      } catch { /* silent — user can pick manually */ }
      const next = new URLSearchParams(location.search);
      next.delete('shared');
      setParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Full form reset. Wipes every user-entered field, the reference chip
   * (both catalog + local file), the AI-drafted description, and the URL
   * search params that seed the reference on load — otherwise a
   * back-nav or reload would immediately restore the old reference.
   *
   * Deliberately does NOT touch: language selector (session preference),
   * `withCover` (kept for user's usual habit), presets applied via the
   * PresetPicker (uh — actually those DO reset since they influence
   * output, so include them). Also skips clearing `currentJobId` so if
   * a generation is running, the Done card still renders — the "New"
   * button is guarded by isGenerating anyway.
   */
  const clearAll = () => {
    setPrompt('');
    setLyrics('');
    setInstrumental(false);
    setSoundPresets([]);
    setLyricsPresets([]);
    setReference(null);
    clearLocalReference();
    setInspireDescription('');
    setError(null);
    // Drop the reference-seed URL params so a reload/back-nav lands on
    // a blank generator. `replace: true` so this doesn't add to history.
    if (params.toString()) setParams(new URLSearchParams(), { replace: true });
  };

  const { data: jobData } = useGetJobQuery(currentJobId!, {
    skip: !currentJobId,
    pollingInterval: currentJobId ? 3000 : 0,
  });

  const currentJob = jobData?.data;
  const isJobComplete = currentJob?.status === 'completed' || currentJob?.status === 'failed';
  const isJobQueued = currentJob?.status === 'queued';
  if (isGenerating && isJobComplete) setIsGenerating(false);

  // When the job finishes, autoplay the result through the global player so
  // the Done card doesn't need its own <audio>. `autoloadedForJobId` guards
  // against re-loading the queue on every poll after the initial completion.
  //
  // The `currentJob._id === currentJobId` check matters: right after the
  // user hits Generate a second time we clear `currentJobId` and re-set it
  // to the new job's id, but RTK Query hasn't fetched the new job yet, so
  // `currentJob` still points at the PREVIOUS job (status=completed). Without
  // the id-match guard this effect would fire with stale data, restart the
  // previous song, and mark `autoloadedForJobId = newJobId` — which then
  // blocks the real auto-load when the new job's data finally arrives.
  const [autoloadedForJobId, setAutoloadedForJobId] = useState<string | null>(null);
  useEffect(() => {
    if (
      currentJob?.status === 'completed'
      && currentJobId
      && String(currentJob._id || '') === String(currentJobId)
      && currentJobId !== autoloadedForJobId
    ) {
      const audioUrl = currentJob.outputAudioUrl || currentJob.outputUrl;
      if (audioUrl) {
        dispatch(setQueue({
          tracks: [{
            id: String(currentJob._id || currentJobId),
            title: currentJob.title || t('generator.untitled', 'MixGenie song'),
            artist: 'MixGenie',
            audioUrl,
            coverUrl: currentJob.coverImageUrl || undefined,
            // Stash lyrics + word-timed lyrics on the queue track so
            // features that consume it later (video studio karaoke, etc.)
            // don't have to re-fetch the job doc. HistoryPage does the same
            // — keeping shapes identical avoids "works from My Songs but
            // not from a fresh generation" surprises.
            meta: {
              jobId: String(currentJob._id || currentJobId),
              lyrics: currentJob.lyrics || undefined,
              syncedLyrics: currentJob.syncedLyrics || undefined,
            },
          }],
          startIndex: 0,
        }));
        setAutoloadedForJobId(currentJobId);
      }
    }
  }, [currentJob, currentJobId, autoloadedForJobId, dispatch, t]);

  // Cover generation runs AFTER the song completes (opted-in +1 credit),
  // so the queue is usually seeded with `coverUrl: undefined` above. When
  // the poll returns a fresh coverImageUrl, mirror it into the player
  // track — otherwise the mini + fullscreen players stay stuck on the
  // fallback gradient until the user navigates away and back to /songs.
  useEffect(() => {
    if (!currentJobId || !currentJob?.coverImageUrl) return;
    dispatch(updateTrack({
      id: String(currentJob._id || currentJobId),
      patch: { coverUrl: currentJob.coverImageUrl, title: currentJob.title || undefined },
    }));
  }, [currentJobId, currentJob?.coverImageUrl, currentJob?.title, currentJob?._id, dispatch]);

  // Mirror lyrics + word-timed syncedLyrics into the queue as later polls
  // fill them in. Mureka's `lyrics_sections` sometimes arrives on a poll
  // AFTER the initial completed transition — without this the video studio
  // opened from the just-generated song would render estimated karaoke
  // even when the BE has real timing on the second poll.
  useEffect(() => {
    if (!currentJobId) return;
    const lyricsPresent = currentJob?.lyrics || currentJob?.syncedLyrics;
    if (!lyricsPresent) return;
    dispatch(updateTrack({
      id: String(currentJob._id || currentJobId),
      patch: {
        meta: {
          jobId: String(currentJob._id || currentJobId),
          lyrics: currentJob.lyrics || undefined,
          syncedLyrics: currentJob.syncedLyrics || undefined,
        },
      },
    }));
  }, [currentJobId, currentJob?.lyrics, currentJob?.syncedLyrics, currentJob?._id, dispatch]);

  const removeReference = () => {
    setReference(null);
    // Strip reference params so a page refresh doesn't restore it.
    ['referenceMode', 'referenceTitle', 'referenceArtist', 'referenceSlug', 'referenceArtistSlug', 'referenceCover', 'vibe', 'styleNotes']
      .forEach((k) => params.delete(k));
    setParams(params, { replace: true });
  };

  // Base signal must be present (prompt OR reference OR local reference OR
  // a sound preset the user tapped). Additionally, vocal generation needs
  // SOMETHING to sing — user lyrics, a lyric-theme preset, or instrumental.
  // Without one of those Mureka fabricates lyrics from the style text
  // (the "sings the reference-song title" bug).
  const hasBaseSignal = prompt.trim().length > 0 || !!reference || !!localReference || soundPresets.length > 0;
  const hasLyricsIntent = instrumental || lyrics.trim().length > 0 || lyricsPresets.length > 0;
  const canGenerate = hasBaseSignal && hasLyricsIntent && !isGenerating;

  // Something is worth showing in the Selection strip. When false we hide
  // the whole strip — an "add a prompt, tap a preset…" empty state is noise.
  const hasAnySelection =
    !!reference || !!localReference || prompt.trim().length > 0 || instrumental
    || soundPresets.length > 0 || lyricsPresets.length > 0;

  // Copy on the sticky Generate button. When disabled, tell the user WHY
  // instead of showing a dead "Generate song" — the classic mobile-form
  // frustration ("I tapped it, nothing happened") kicks in otherwise.
  const generateLabel = (() => {
    if (currentJobId && isJobComplete) return t('generator.generateAnother', 'Generate another');
    if (isGenerating) return t('generator.working', 'Generating…');
    if (!hasBaseSignal) return t('generator.needBase', 'Add a prompt or a reference');
    if (!hasLyricsIntent) return t('generator.needLyrics', 'Add lyrics or tick Instrumental');
    return t('generator.generate', 'Generate song');
  })();

  /**
   * Fold applied preset fragments into the user's prompts. Prompt fragments
   * are appended (comma-separated) so the user's own words lead. Lyric-theme
   * fragments become a bracketed brief ahead of the user's lyrics so Mureka
   * sees the intent even when the user hasn't drafted anything yet.
   */
  const composeStyle = (): string => {
    const base = prompt.trim();
    const fragments = soundPresets.map((p) => p.promptFragment).filter(Boolean);
    return [base, ...fragments].filter(Boolean).join('. ');
  };
  /**
   * The lyrics we hand to Mureka are the user's textarea contents verbatim
   * — Mureka SINGS this text, so it must not contain meta-instructions like
   * "[Theme: birthday ...]" or the model will try to sing those words.
   * Themes drive the auto-draft (which produces real lyrics); after that
   * the textarea is the single source of truth.
   */
  const composeLyrics = (): string | undefined => {
    if (instrumental) return undefined;
    return lyrics.trim() || undefined;
  };

  /**
   * Ask PromptLab to draft lyrics. `themePresets` (when passed) win as the
   * primary brief so that picking "Birthday" from the lyric-theme picker
   * generates a birthday song without needing a prompt or reference set —
   * that's the whole point of the presets. When no theme is passed we fall
   * back to the current field state (prompt / reference).
   */
  const handleDraftLyrics = async (themePresets?: PresetDoc[]) => {
    const themes = themePresets ?? lyricsPresets;
    const themeBrief = themes.map((p) => p.promptFragment).filter(Boolean).join(' · ');
    try {
      const res: any = await draftLyrics({
        // `brief` takes precedence on the BE — hand it the theme text so
        // even a bare "Birthday" selection produces lyrics.
        brief: themeBrief || undefined,
        prompt: prompt.trim() || undefined,
        referenceTitle: reference?.title || undefined,
        referenceArtist: reference?.artist || undefined,
        // Language chosen in the picker next to the Draft button. Backend
        // takes the English name of the language (Spanish/English/Portuguese)
        // and passes it to the lyrics-generation prompt.
        language: LANG_NAMES[lyricsLanguage] || 'Spanish',
      }).unwrap();
      const drafted = res?.data?.lyrics || res?.lyrics;
      if (drafted) {
        setLyrics(drafted);
        setInstrumental(false);
      } else {
        toast({ title: t('generator.lyricsDraftEmpty', 'Could not draft lyrics — try adding a prompt or a theme.'), status: 'warning' });
      }
    } catch (err: any) {
      toast({ title: err?.data?.message || t('generator.lyricsDraftFailed', 'Draft failed'), status: 'error' });
    }
  };

  /**
   * Handler for the lyric-theme picker's Apply. Sets the applied presets
   * AND auto-drafts fresh lyrics from those themes — user asked for
   * "select a lyrics theme → lyrics generated with just that". We only
   * auto-draft when new themes were actually added (removing a theme
   * should leave the current lyrics alone).
   */
  const applyLyricsPresets = (next: PresetDoc[]) => {
    const prevIds = new Set(lyricsPresets.map((p) => String(p._id)));
    const added = next.filter((p) => !prevIds.has(String(p._id)));
    setLyricsPresets(next);
    if (added.length > 0) {
      // Draft against the FULL new selection (not just the added subset)
      // so multi-theme composition ("Birthday" + "For my love") works.
      handleDraftLyrics(next);
    }
  };

  // Timestamp of the last submit. Guards against Mureka 429s when the user
  // rage-taps "Generate another" — Mureka rate-limits per-account and each
  // 429 still burns a network round-trip. 3s is enough to defeat double-tap
  // while feeling instantaneous under normal use.
  const lastSubmitRef = useRef<number>(0);
  const handleGenerate = async () => {
    if (!canGenerate) {
      setError(t('generator.needPromptOrReference', 'Add a prompt or a reference song to generate.'));
      return;
    }
    const now = Date.now();
    if (now - lastSubmitRef.current < 3000) {
      setError(t('generator.tooFast', 'Whoa — hold on a couple of seconds before generating again.'));
      return;
    }
    lastSubmitRef.current = now;
    setError(null);
    setIsGenerating(true);
    // Reset previous job state so the status card doesn't show "Done" from
    // the last run while the new one submits, and so the auto-play effect
    // fires again once the new job completes (autoloadedForJobId guards
    // against re-loading the same track, so it needs to be cleared here).
    setCurrentJobId(null);
    setAutoloadedForJobId(null);

    // Compose the final style/lyrics text (user text + applied presets),
    // then fall back to a reference-derived description if the whole style
    // string would still be empty.
    const composedStyle = composeStyle();
    const composedLyrics = composeLyrics();
    const vibe = composedStyle
      || (reference ? `Create an original song inspired by ${reference.title} by ${reference.artist}.` : '');

    try {
      // Local reference wins — user-picked file gets uploaded verbatim as
      // referenceAudio; backend then skips the iTunes lookup because the
      // referenceAudio field is already populated.
      const referenceAudio = localReference ? await fileToReferenceAudio(localReference.file) : undefined;

      const jobPayload: any = {
        email: user?.email,
        userId: user?.id || user?._id,
        vibe,
        prompt: composedStyle || undefined,
        // The user's OWN typed text only — no preset fragments folded in.
        // Backend uses this for the auto-title so tapping "Hopeful" doesn't
        // name every song "makes the listener feel hopeful...".
        userPromptText: prompt.trim() || undefined,
        instrumental,
        generateCover: withCover,
        lyrics: composedLyrics,
        // Language of the vocals. Backend threads this into the style
        // synthesizer so Mureka is told to sing in the right language
        // (matters most when lyrics are drafted at generation time or
        // when they're multilingual/ambiguous).
        lyricsLanguage: !instrumental ? (LANG_NAMES[lyricsLanguage] || undefined) : undefined,
        referenceMode: (localReference || reference) ? (localReference ? 'audio-snippet' : 'title-artist') : undefined,
        referenceTitle: reference?.title || undefined,
        referenceArtist: reference?.artist || undefined,
        referenceSlug: reference?.slug || undefined,
        referenceArtistSlug: reference?.artistSlug || undefined,
        styleNotes: reference?.styleNotes || undefined,
        referenceAudio,
      };
      const result = await createJob(jobPayload).unwrap();
      if (result?.code === 'INSUFFICIENT_CREDITS') {
        paywall.onOpen();
        setIsGenerating(false);
        return;
      }
      setCurrentJobId(result?.data?.id || result?.data?._id);
    } catch (err: any) {
      if (err?.data?.code === 'INSUFFICIENT_CREDITS' || err?.status === 402) {
        paywall.onOpen();
      } else if (err?.data?.code === 'QUEUE_FULL_UPGRADE' || err?.status === 429) {
        // Free-tier per-user cap hit — a natural upsell moment. Distinct
        // from the out-of-credits paywall: the user CAN still generate
        // eventually, they just need to wait or upgrade for parallel.
        setQueueFull({ queued: err?.data?.queued, limit: err?.data?.limit });
      } else {
        setError(err?.data?.message || t('generator.failed', 'Generation failed. Please try again.'));
      }
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Container
        maxW="640px"
        pt={{ base: 2, md: 4 }}
        pb={{ base: '160px', md: '140px' }}
        px={0}
      >
        <Stack spacing={2} mb={7} align="stretch">
          <HStack justify="space-between" align="flex-start" spacing={3}>
            <Heading
              as="h1"
              fontSize={{ base: '30px', md: '36px' }}
              fontWeight={800}
              lineHeight="1.1"
              letterSpacing="-0.015em"
              color="whiteAlpha.900"
              style={{ textWrap: 'balance' as any }}
              flex="1"
              minW={0}
            >
              {t('generator.title', 'Create your song')}
            </Heading>
            {/* Full reset — wipe every user-entered field and the URL
                params that seed the reference. Used when the user has
                been iterating on one brief and wants a truly blank slate
                (a different song, not a variation). Disabled while a
                generation is in flight to avoid discarding params
                mid-request. */}
            <Button
              onClick={clearAll}
              isDisabled={isGenerating || isInspiring}
              size="sm"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ bg: 'whiteAlpha.100', color: 'whiteAlpha.900' }}
              flexShrink={0}
              data-cta="generator:clear-all"
            >
              ✨ {t('generator.newBrief', 'New')}
            </Button>
          </HStack>
        </Stack>

        {/* --- Inspired by an image — vision LLM drafts a full brief -----
             A DIFFERENT axis than "reference song": the image doesn't stay
             attached to the generation, it just seeds prompt + lyrics.
             Deliberately placed before the reference block so it reads as
             an alternative *starting point*, not another kind of reference.
             `capture="environment"` asks Android/iOS to include camera in
             the picker sheet next to the gallery. */}
        <Box mb={3}>
          <Button
            onClick={pickInspirationImage}
            isDisabled={isInspiring}
            w="full"
            h="auto"
            py={2.5}
            px={3.5}
            bg="rgba(233, 74, 143, 0.08)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="whiteAlpha.150"
            borderRadius="12px"
            _hover={{ bg: 'rgba(233, 74, 143, 0.14)', borderColor: 'pink.400' }}
            _active={{ transform: 'scale(0.99)' }}
            _disabled={{ opacity: 0.7, cursor: 'wait' }}
            data-cta="generator:inspire-from-image"
          >
            <HStack spacing={2.5} w="full" justify="flex-start" align="center">
              {isInspiring ? <Spinner size="sm" color="pink.300" /> : <Text fontSize="lg">🖼️</Text>}
              <Text fontSize="sm" fontWeight={600} noOfLines={1} flex="1" textAlign="left">
                {isInspiring
                  ? t('generator.inspireBusy', 'Reading the image…')
                  : t('generator.inspireCtaShort', 'Draft a song from a photo')}
              </Text>
            </HStack>
          </Button>
          {inspireDescription && !isInspiring && (
            <Box
              mt={2}
              p={3}
              bg="rgba(10, 6, 22, 0.55)"
              border="1px solid"
              borderColor="whiteAlpha.150"
              borderRadius="10px"
            >
              <Text fontSize="xs" color="whiteAlpha.600" mb={1} textTransform="uppercase" letterSpacing="0.06em">
                {t('generator.inspireReading', 'MixGenie sees')}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.900" lineHeight="1.5">
                {inspireDescription}
              </Text>
            </Box>
          )}
          <Input
            ref={inspireImageInput}
            type="file"
            accept="image/*"
            // Deliberately NO `capture` — on Chrome/Android that attribute
            // FORCES the camera and skips the gallery. Without it, the OS
            // picker offers gallery, camera and files together.
            display="none"
            onChange={onInspirationImageChosen}
          />
        </Box>

        {/* --- Reference song — backend-searched, iTunes preview seeds Mureka --- */}
        <FieldBlock
          label={t('generator.referenceLabel', 'Reference song')}
          hint={
            (reference || localReference)
              ? undefined
              : t('generator.referenceHintShort', 'Optional — search, pick, or record a track to base yours on.')
          }
        >
          {reference ? (
            <Stack spacing={2} align="flex-start">
              <HStack
                spacing={2}
                pl={reference.cover ? 1 : 3}
                pr={1}
                py={1}
                bg="pink.900"
                color="whiteAlpha.900"
                borderRadius="full"
                borderWidth="1px"
                borderColor="pink.500"
                maxW="100%"
                w="fit-content"
              >
                {reference.cover && (
                  <Image
                    src={catalogCoverImage(reference.cover, 'song')}
                    alt=""
                    boxSize="32px"
                    borderRadius="full"
                    objectFit="cover"
                  />
                )}
                <Stack spacing={0} minW={0} pr={2}>
                  <Text fontSize="xs" fontWeight={700} noOfLines={1}>🎵 {reference.title}</Text>
                  {reference.artist && (
                    <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>{reference.artist}</Text>
                  )}
                </Stack>
                <CloseButton size="sm" onClick={removeReference} aria-label="Remove reference" />
              </HStack>
              {localReference && (
                <HStack spacing={2} fontSize="xs" color="whiteAlpha.700" pl={1}>
                  <Text>📁 {t('generator.referenceUsingLocal', 'Using your file as the audio seed')} — {localReference.title || localReference.name}</Text>
                  <CloseButton size="xs" onClick={clearLocalReference} aria-label="Remove local file" />
                </HStack>
              )}
            </Stack>
          ) : localReference ? (
            <HStack
              spacing={2}
              pl={3}
              pr={1}
              py={1}
              bg="pink.900"
              color="whiteAlpha.900"
              borderRadius="full"
              borderWidth="1px"
              borderColor="pink.500"
              maxW="100%"
              w="fit-content"
            >
              <Stack spacing={0} minW={0} pr={2}>
                <Text fontSize="xs" fontWeight={700} noOfLines={1}>
                  📁 {localReference.title || localReference.name}
                </Text>
                {localReference.artist && (
                  <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>{localReference.artist}</Text>
                )}
              </Stack>
              <CloseButton size="sm" onClick={clearLocalReference} aria-label="Remove local reference" />
            </HStack>
          ) : (
            <Stack spacing={2}>
              <ReferenceSearch
                onPick={(song) => {
                  setReference({
                    title: song.title,
                    artist: song.artist,
                    slug: song.slug || '',
                    artistSlug: song.artistSlug || '',
                    cover: song.cover || '',
                    styleNotes: song.styleNotes || '',
                  });
                  clearLocalReference();
                }}
              />
              {/* Escape hatches when the catalog search doesn't cover it:
                  pick an audio file from the device, or record a snippet
                  with the mic (Shazam-style — hum a tune, play a snippet
                  on another device). Both funnel into the same
                  localReference redux slice. */}
              <HStack spacing={2} justify="center" py={1} flexWrap="wrap">
                <Text fontSize="xs" color="whiteAlpha.500">or</Text>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="whiteAlpha"
                  color="whiteAlpha.900"
                  borderColor="whiteAlpha.300"
                  onClick={() => referenceFileInput.current?.click()}
                  data-cta="generator:pick-device-file"
                  leftIcon={<Text as="span" fontSize="14px">📁</Text>}
                >
                  {t('generator.pickDeviceFile', 'Pick from device')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="whiteAlpha"
                  color="whiteAlpha.900"
                  borderColor="whiteAlpha.300"
                  onClick={micRecorder.onOpen}
                  data-cta="generator:mic-record"
                  leftIcon={
                    <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="currentColor">
                      <path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" />
                      <path d="M19 11a1 1 0 10-2 0 5 5 0 01-10 0 1 1 0 10-2 0 7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11z" />
                    </Box>
                  }
                >
                  {t('generator.recordMic', 'Record with mic')}
                </Button>
              </HStack>
              <Input
                ref={referenceFileInput}
                type="file"
                accept="audio/*"
                display="none"
                onChange={onReferenceFileChosen}
              />
            </Stack>
          )}
        </FieldBlock>

        {/* --- Optional prompt + sound presets --- */}
        <FieldBlock
          label={t('generator.promptLabel', 'Prompt')}
          hint={undefined}
          right={
            <PresetPicker
              target="sound"
              selectedIds={soundPresets.map((p) => String(p._id))}
              onApply={setSoundPresets}
              triggerLabel={t('generator.addSoundPreset', 'Add style / instrument')}
            />
          }
        >
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('generator.promptPlaceholder', 'e.g. Dark synth-wave with driving bass, cinematic feel, 2020s production')}
            bg="rgba(10, 6, 22, 0.55)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="12px"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)', bg: 'rgba(10, 6, 22, 0.75)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
            rows={3}
            resize="vertical"
          />
          {soundPresets.length > 0 && (
            <Box mt={3}>
              <PresetChips
                presets={soundPresets}
                onRemove={(id) => setSoundPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            </Box>
          )}
        </FieldBlock>

        {/* --- Lyrics + instrumental toggle + draft-lyrics button --- */}
        <FieldBlock
          label={t('generator.lyricsLabel', 'Lyrics')}
          hint={undefined}
          right={
            <HStack spacing={3} wrap="wrap">
              {!instrumental && (
                <PresetPicker
                  target="lyrics"
                  selectedIds={lyricsPresets.map((p) => String(p._id))}
                  onApply={applyLyricsPresets}
                  triggerLabel={t('generator.addLyricTheme', 'Add lyric theme')}
                />
              )}
              {!instrumental && (
                <Select
                  size="xs"
                  value={lyricsLanguage}
                  onChange={(e) => setLyricsLanguage(e.target.value)}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  color="whiteAlpha.900"
                  width="auto"
                  aria-label={t('generator.lyricsLanguageLabel', 'Language')}
                >
                  <option value="es" style={{ color: '#000' }}>🇪🇸 Español</option>
                  <option value="en" style={{ color: '#000' }}>🇬🇧 English</option>
                  <option value="pt" style={{ color: '#000' }}>🇵🇹 Português</option>
                </Select>
              )}
              {!instrumental && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="pink"
                  onClick={() => handleDraftLyrics()}
                  isLoading={isDraftingLyrics}
                  // Enable whenever there's ANY brief we can hand to the LLM
                  // — including a lyric theme on its own.
                  isDisabled={!hasBaseSignal && lyricsPresets.length === 0}
                  data-cta="generator:draft-lyrics"
                >
                  ✨ {t('generator.draftLyrics', 'Draft lyrics')}
                </Button>
              )}
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="instrumental-switch"
                  isChecked={instrumental}
                  onChange={(e) => {
                    setInstrumental(e.target.checked);
                    if (e.target.checked) setLyricsPresets([]);
                  }}
                  colorScheme="pink"
                  size="sm"
                />
                <FormLabel
                  htmlFor="instrumental-switch"
                  mb={0}
                  color="whiteAlpha.800"
                  fontSize="sm"
                  fontWeight={500}
                  cursor="pointer"
                >
                  {t('generator.instrumental', 'Instrumental only')}
                </FormLabel>
              </FormControl>
            </HStack>
          }
        >
          <Textarea
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            placeholder={
              instrumental
                ? t('generator.instrumentalOn', 'Instrumental selected — lyrics disabled.')
                : t('generator.lyricsPlaceholder', 'Write or paste your lyrics here. Click ✨ Draft lyrics to have MixGenie propose some.')
            }
            bg="rgba(10, 6, 22, 0.55)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="12px"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)', bg: 'rgba(10, 6, 22, 0.75)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
            rows={8}
            resize="vertical"
            isDisabled={instrumental}
            opacity={instrumental ? 0.5 : 1}
          />
          {!instrumental && lyricsPresets.length > 0 && (
            <Box mt={3}>
              <PresetChips
                presets={lyricsPresets}
                onRemove={(id) => setLyricsPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            </Box>
          )}
        </FieldBlock>

        {/* --- Selection chip strip — only rendered when there's actually
             something to summarise. The old empty state ("Nothing selected
             yet — add a prompt…") was noise on a page full of empty inputs. */}
        {hasAnySelection && (
          <Flex
            mb={4}
            wrap="wrap"
            gap={2}
            align="center"
            p={2.5}
            bg="rgba(20, 14, 36, 0.5)"
            borderRadius="14px"
            border="1px solid"
            borderColor="whiteAlpha.100"
          >
            {localReference && (
              <HStack spacing={2} pl={3} pr={1} py={1} bg="accent.500" color="white" borderRadius="full" borderWidth="1px" borderColor="accent.400" maxW="100%">
                <Text fontSize="xs" fontWeight={700} noOfLines={1}>📁 {localReference.name}</Text>
                <CloseButton size="sm" onClick={clearLocalReference} aria-label="Remove local reference" />
              </HStack>
            )}
            {!localReference && reference && (
              <ReferenceChip
                title={reference.title}
                artist={reference.artist}
                cover={reference.cover}
                onRemove={removeReference}
              />
            )}
            {prompt.trim() && (
              <Badge
                px={3} py={2} borderRadius="full" bg="accent.500" color="white"
                fontWeight={600} textTransform="none" fontSize="sm" maxW="100%"
              >
                <Text as="span" noOfLines={1}>💬 {prompt.trim()}</Text>
              </Badge>
            )}
            {instrumental && (
              <Badge px={3} py={2} borderRadius="full" colorScheme="purple" textTransform="none" fontSize="sm">🎹 instrumental</Badge>
            )}
            {soundPresets.length > 0 && (
              <PresetChips
                presets={soundPresets}
                onRemove={(id) => setSoundPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            )}
            {!instrumental && lyricsPresets.length > 0 && (
              <PresetChips
                presets={lyricsPresets}
                onRemove={(id) => setLyricsPresets((prev) => prev.filter((p) => String(p._id) !== id))}
              />
            )}
          </Flex>
        )}

        {/* --- Generate error (sticky footer holds the button itself) --- */}
        {error && (
          <Alert status="error" borderRadius="10px" mb={4} bg="red.900" color="whiteAlpha.900" borderWidth="1px" borderColor="red.500">
            <AlertIcon color="red.300" />
            {error}
          </Alert>
        )}

        {/* --- Job status --- */}
        {currentJobId && (
          <Box
            mt={7}
            bg="rgba(20, 14, 36, 0.65)"
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="18px"
            p={{ base: 4, md: 5 }}
            boxShadow="0 8px 24px rgba(0,0,0,0.28)"
          >
            <HStack spacing={3} mb={currentJob?.status === 'completed' && (currentJob?.outputAudioUrl || currentJob?.outputUrl) ? 4 : 0}>
              {currentJob?.status === 'failed' && (
                <Image
                  src={failArtFor(String(currentJobId))}
                  boxSize="72px"
                  borderRadius="10px"
                  objectFit="cover"
                  alt={t('generator.jobFailed', 'Generation failed')}
                  flexShrink={0}
                />
              )}
              {isJobComplete ? (
                <CheckCircleIcon color={currentJob?.status === 'completed' ? 'green.400' : 'red.400'} />
              ) : (
                <Spinner size="sm" color="accent.400" />
              )}
              <Stack spacing={0} flex="1">
                <Text color="whiteAlpha.900" fontWeight={600}>
                  {isJobComplete
                    ? currentJob?.status === 'completed'
                      ? t('generator.done', 'Done')
                      : t('generator.jobFailed', 'Generation failed')
                    : t('generator.working', 'Generating…')}
                </Text>
                <Text color="whiteAlpha.600" fontSize="xs">Job #{currentJobId.slice(-8)}</Text>
              </Stack>
              {isJobComplete && (
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    colorScheme="pink"
                    variant="solid"
                    onClick={handleGenerate}
                    isDisabled={!canGenerate}
                    data-cta="generator:submit-again"
                  >
                    ↻ {t('generator.generateAnother', 'Generate another')}
                  </Button>
                  <Button as={Link} to="/songs" size="sm" variant="outline" colorScheme="whiteAlpha">
                    {t('generator.viewAll', 'My songs')}
                  </Button>
                </HStack>
              )}
            </HStack>
            {currentJob?.status === 'completed' && (currentJob?.outputAudioUrl || currentJob?.outputUrl) && (
              <Box bg="whiteAlpha.50" borderRadius="8px" p={3} borderWidth="1px" borderColor="whiteAlpha.100">
                <Text fontSize="sm" color="whiteAlpha.700">
                  {t('generator.nowPlaying', '🎵 Playing in the bottom player — use it to pause, seek and control volume.')}
                </Text>
                <HStack mt={2} spacing={4} fontSize="xs">
                  <Text
                    as="button"
                    color="accent.400"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => downloadSongWithTags(
                      currentJob.outputAudioUrl || currentJob.outputUrl,
                      currentJob.title || t('generator.untitled', 'MixGenie song'),
                    )}
                    data-cta="generator:download"
                  >
                    ⬇ {t('generator.download', 'Download')}
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* --- Sticky Generate footer ---
           Primary action anchored to thumb reach so users never scroll to
           submit. Sits ABOVE the AppShell bottom nav (68px + safe-area) and
           carries the credit cost inline on the button so cost + action live
           together. Hidden once a job completes — the Done card in-flow has
           its own Generate-another action and stacking three fixed bars
           (player + nav + this) crushes the viewport. */}
      {!(currentJobId && isJobComplete) && (
        <Box
          position="fixed"
          left={0}
          right={0}
          bottom={`calc(env(safe-area-inset-bottom, 0px) + 68px)`}
          zIndex={16}
          bgGradient="linear(to-t, rgba(15, 11, 31, 0.98) 60%, rgba(15, 11, 31, 0))"
          pt={5}
          pb={2}
          px={3}
          pointerEvents="none"
        >
          <Container maxW="640px" px={0} sx={{ pointerEvents: 'auto' }}>
            <HStack justify="space-between" align="center" mb={2} px={1}>
              <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                <Switch
                  id="cover-switch"
                  isChecked={withCover}
                  onChange={(e) => setWithCover(e.target.checked)}
                  colorScheme="pink"
                  size="sm"
                />
                <FormLabel
                  htmlFor="cover-switch"
                  mb={0}
                  cursor="pointer"
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  fontWeight={500}
                >
                  <PictoolsMark size={14} />
                  <Text as="span" fontSize="xs" color="whiteAlpha.800">
                    {t('generator.withCoverShort', '+ Cover art')}
                  </Text>
                </FormLabel>
              </FormControl>
              <HStack
                spacing={1}
                color="whiteAlpha.600"
                fontSize="xs"
                fontFamily="ui-monospace, SF Mono, Menlo, monospace"
                letterSpacing="0.05em"
              >
                <Text>⚡ {withCover ? '2' : '1'}</Text>
              </HStack>
            </HStack>
            <Button
              w="full"
              h="54px"
              onClick={handleGenerate}
              isLoading={isGenerating}
              loadingText={t('generator.working', 'Generating…')}
              isDisabled={!canGenerate && !isGenerating}
              data-cta="generator:submit"
              borderRadius="14px"
              bgGradient={canGenerate
                ? 'linear(90deg, #E94A8F 0%, #7354FF 100%)'
                : 'linear(90deg, #3A2740 0%, #2E2650 100%)'}
              color={canGenerate ? 'white' : 'whiteAlpha.600'}
              fontWeight={700}
              fontSize="16px"
              letterSpacing="0.01em"
              boxShadow={canGenerate ? '0 12px 32px rgba(233,74,143,0.35)' : 'none'}
              _hover={canGenerate ? {
                bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 16px 40px rgba(233,74,143,0.45)',
              } : {}}
              _active={{ transform: 'translateY(0)' }}
              _disabled={{
                bgGradient: 'linear(90deg, #3A2740 0%, #2E2650 100%)',
                color: 'whiteAlpha.600',
                boxShadow: 'none',
                cursor: 'not-allowed',
                opacity: 1,
                _hover: {
                  bgGradient: 'linear(90deg, #3A2740 0%, #2E2650 100%)',
                  transform: 'none',
                },
              }}
            >
              {generateLabel}
            </Button>
          </Container>
        </Box>
      )}

      {/* --- Out-of-credits paywall --- */}
      <Modal isOpen={paywall.isOpen} onClose={paywall.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>{t('generator.paywallTitle', "You're out of credits")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} color="whiteAlpha.800">
              {t('generator.paywallBody', 'Grab a credit pack or subscribe to keep generating.')}
            </Text>
            <List spacing={2} fontSize="sm">
              {[t('generator.pw1', 'Unlimited song generation'), t('generator.pw2', 'Priority queue'), t('generator.pw3', 'HD downloads')].map((b) => (
                <ListItem key={b}><ListIcon as={CheckCircleIcon} color="accent.400" />{b}</ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="pink" w="full" onClick={() => { paywall.onClose(); navigate('/credits'); }}>
              {t('generator.seePlans', 'See plans')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* --- Free-tier queue cap modal — upsell rather than error.
           Distinct from the out-of-credits paywall: the user CAN still
           generate, they just need to wait for one of their in-flight
           songs to finish, OR upgrade to a plan to run them in parallel. */}
      <Modal isOpen={!!queueFull} onClose={() => setQueueFull(null)} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>
            {t('generator.queueFullTitle', 'You already have songs in the queue')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} color="whiteAlpha.800">
              {queueFull?.limit
                ? t('generator.queueFullBody', 'Free plans can have {{limit}} generations in flight at a time. Wait for one to finish — or upgrade to run more in parallel.', { limit: queueFull.limit })
                : t('generator.queueFullBodyGeneric', 'Wait for one to finish — or upgrade to run more in parallel.')}
            </Text>
            <List spacing={2} fontSize="sm">
              {[t('generator.qf1', 'Skip the free-tier queue'), t('generator.qf2', 'Run multiple generations in parallel'), t('generator.qf3', 'Priority processing')].map((b) => (
                <ListItem key={b}><ListIcon as={CheckCircleIcon} color="accent.400" />{b}</ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" color="whiteAlpha.700" onClick={() => setQueueFull(null)}>
              {t('generator.queueFullWait', "I'll wait")}
            </Button>
            <Button colorScheme="pink" onClick={() => { setQueueFull(null); navigate('/credits'); }}>
              {t('generator.queueFullUpgrade', 'Upgrade')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Full-screen "we're working on it" overlay. Open while the FE is
          submitting (`isGenerating`) OR the server-side job is still in a
          non-terminal state. Cancel dismisses the overlay — it does NOT
          actually kill the Mureka job (no cancel endpoint), it just
          returns the user to the form so they can adjust or wait. */}
      <GenerationOverlay
        isOpen={isGenerating || (!!currentJobId && !isJobComplete)}
        instrumental={instrumental}
        isQueued={isJobQueued}
        onCancel={() => {
          setIsGenerating(false);
          setCurrentJobId(null);
        }}
      />

      {/* Mic recorder — third reference source. On confirm we get an MP3
          File and dispatch it as a local reference via the same slice
          the clip picker uses (localAudio.tracks). Any prior catalog
          reference is cleared because the recording wins. */}
      <MicRecorderModal
        isOpen={micRecorder.isOpen}
        onCancel={micRecorder.onClose}
        onConfirm={(file) => {
          const id = `mic-${Date.now()}`;
          dispatch(addTracks([{
            id,
            file,
            name: file.name,
            size: file.size,
            addedAt: Date.now(),
          }]));
          dispatch(setReferenceId(id));
          setReference(null);
          micRecorder.onClose();
          toast({
            title: t('generator.micAttached', 'Recording attached as reference'),
            status: 'success',
            duration: 2000,
          });
        }}
      />

      {/* Clip picker — opens when a device-picked (or PWA-shared) audio file
          is larger than ~3 MB. User scrubs a 60-second window; on confirm we
          attach just that slice as the reference so we don't ship a full
          multi-minute track to Mureka (which only samples the first ~60 s
          anyway). */}
      <AudioClipPickerModal
        file={clipPending?.file || null}
        onCancel={() => setClipPending(null)}
        onSkip={() => {
          if (clipPending) attachLocalReferenceFile(clipPending.file);
          setClipPending(null);
        }}
        onConfirm={(clippedFile) => {
          attachLocalReferenceFile(clippedFile);
          setClipPending(null);
        }}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// Small building blocks kept in-file — the rest of the app has no other
// consumers, and inlining them keeps the page self-describing.
// -----------------------------------------------------------------------------

function FieldBlock({ label, hint, right, children }: { label: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box
      mb={3}
      bg="rgba(20, 14, 36, 0.6)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="16px"
      p={{ base: 3.5, md: 5 }}
    >
      <Flex align="center" justify="space-between" mb={hint ? 1 : 2} gap={3} wrap="wrap">
        <Text
          fontSize="11px"
          fontWeight={700}
          color="whiteAlpha.700"
          textTransform="uppercase"
          letterSpacing="0.1em"
        >{label}</Text>
        {right}
      </Flex>
      {hint && <Text fontSize="xs" color="whiteAlpha.500" mb={3}>{hint}</Text>}
      {children}
    </Box>
  );
}

function ReferenceChip({ title, artist, cover, onRemove }: { title: string; artist: string; cover?: string; onRemove: () => void }) {
  return (
    <HStack
      spacing={2} pl={cover ? 1 : 3} pr={1} py={1}
      bg="pink.900" color="whiteAlpha.900" borderRadius="full"
      borderWidth="1px" borderColor="pink.500" maxW="100%"
    >
      {cover && (
        <Image
          src={catalogCoverImage(cover, 'song')}
          alt={`${title} by ${artist}`}
          boxSize="28px" borderRadius="full" objectFit="cover"
        />
      )}
      <Stack spacing={0} minW={0}>
        <Text fontSize="xs" fontWeight={700} noOfLines={1}>🎵 {title}</Text>
        <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>{artist}</Text>
      </Stack>
      <CloseButton size="sm" onClick={onRemove} aria-label="Remove reference" />
    </HStack>
  );
}
